import { useCallback, useMemo, useState } from 'react';

import { Table } from 'common-ui/table';
import { formatDateTime } from 'utils/dateUtils';

import {
  Container,
  CustomerIDBadge,
  DateTag,
  OfferCountBadge,
} from './CustomerManagement.styles';
import { Filter } from './components/filter';
import { OfferInfoModal } from './components/offerInfoModal';
import { useCustomerManagementSearchData } from './useSearchData';

const CustomerManagement = () => {
  const { searchData, setCustomerManagementSearchData } = useCustomerManagementSearchData();
  const [isOfferModalOpen, setIsOfferModalOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);

  // Mock data for the customer table
  const mockCustomers = [
    {
      id: 'CUST-001',
      fullName: 'John Smith',
      offer: '1',
      offerDetails: { name: 'Premium Plan', status: 'Active' },
      address: '123 Main St, New York, NY 10001',
      phoneNumber: '+1 (555) 123-4567',
      createdDate: '2024-01-15',
    },
    {
      id: 'CUST-001',
      fullName: 'John Smith',
      offer: '1',
      offerDetails: { name: 'Premium Plan', status: 'Active' },
      address: '123 Main St, New York, NY 10001',
      phoneNumber: '+1 (555) 123-4567',
      createdDate: '2024-01-15',
    },
    {
      id: 'CUST-002',
      fullName: 'Sarah Johnson',
      offer: '1',
      offerDetails: { name: 'Basic Plan', status: 'Disabled' },
      address: '456 Oak Ave, Los Angeles, CA 90001',
      phoneNumber: '+1 (555) 234-5678',
      createdDate: '2024-01-18',
    },
    {
      id: 'CUST-003',
      fullName: 'Michael Brown',
      offer: '1',
      offerDetails: { name: 'Standard Plan', status: 'Active' },
      address: '789 Pine Rd, Chicago, IL 60601',
      phoneNumber: '+1 (555) 345-6789',
      createdDate: '2024-02-02',
    },
    {
      id: 'CUST-004',
      fullName: 'Emily Davis',
      offer: '1',
      offerDetails: { name: 'Enterprise Plan', status: 'Active' },
      address: '321 Elm St, Houston, TX 77001',
      phoneNumber: '+1 (555) 456-7890',
      createdDate: '2024-02-10',
    },
    {
      id: 'CUST-005',
      fullName: 'David Wilson',
      offer: '1',
      offerDetails: { name: 'Premium Plan', status: 'Disabled' },
      address: '654 Maple Dr, Phoenix, AZ 85001',
      phoneNumber: '+1 (555) 567-8901',
      createdDate: '2024-02-15',
    },
    {
      id: 'CUST-006',
      fullName: 'Jennifer Martinez',
      offer: '1',
      offerDetails: { name: 'Standard Plan', status: 'Active' },
      address: '987 Cedar Ln, Philadelphia, PA 19101',
      phoneNumber: '+1 (555) 678-9012',
      createdDate: '2024-02-20',
    },
    {
      id: 'CUST-007',
      fullName: 'Robert Anderson',
      offer: '1',
      offerDetails: { name: 'Basic Plan', status: 'Active' },
      address: '147 Birch Ct, San Antonio, TX 78201',
      phoneNumber: '+1 (555) 789-0123',
      createdDate: '2024-03-01',
    },
    {
      id: 'CUST-008',
      fullName: 'Lisa Taylor',
      offer: '1',
      offerDetails: { name: 'Premium Plan', status: 'Active' },
      address: '258 Spruce St, San Diego, CA 92101',
      phoneNumber: '+1 (555) 890-1234',
      createdDate: '2024-03-05',
    },
  ];

  const handleOfferClick = useCallback((offerDetails) => {
    setSelectedOffer(offerDetails);
    setIsOfferModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsOfferModalOpen(false);
    setSelectedOffer(null);
  }, []);

  const tableColumns = useMemo(
    () => [
      {
        title: 'Customer ID',
        key: 'id',
        dataIndex: 'id',
        render: (id) => <CustomerIDBadge>{id}</CustomerIDBadge>,
      },
      {
        title: 'Full Name',
        key: 'fullName',
        dataIndex: 'fullName',
      },
      {
        title: 'Offer',
        key: 'offer',
        dataIndex: 'offer',
        render: (id, record) => (
          <OfferCountBadge
            onClick={() => handleOfferClick(record.offerDetails)}
            style={{ cursor: 'pointer' }}
          >
            {id}
          </OfferCountBadge>
        ),
      },
      {
        title: 'Address',
        key: 'address',
        dataIndex: 'address',
      },
      {
        title: 'Phone Number',
        key: 'phoneNumber',
        dataIndex: 'phoneNumber',
      },
      {
        title: 'Created Date',
        key: 'createdDate',
        dataIndex: 'createdDate',
        render: (createdDate) => (
          <DateTag type="dateTag" variant={formatDateTime(createdDate)}>
            {formatDateTime(createdDate)}
          </DateTag>
        ),
      },
    ],
    [handleOfferClick]
  );

  return (
    <Container>
      <Filter searchData={searchData} setSearchData={setCustomerManagementSearchData} />
      <Table columns={tableColumns} data={mockCustomers} />

      <OfferInfoModal
        isOpen={isOfferModalOpen}
        onClose={handleCloseModal}
        offerDetails={selectedOffer}
      />
    </Container>
  );
};

export default CustomerManagement;
